<template>
  <div class="job-list">
    <job-filters v-if="filterable && showLeftMenu"/>

    <data-table :service="JobService"
                :entity="entity"
                :columns="columns"
                :filters="jobFilters"
                :go-to-details-on-row-click="false"
                :go-to-add-on-click="goToAddOnClick"
                :filterable="filterable"
                operationsAlign="right"
                @on-row-click="onRowClick"
                @on-add="onJobAdd"
                ref="table">
      <template v-slot:extra-buttons="{row}">
        <template v-if="row.status === 'check'">
          <tooltip-icon-button type="success"
                               :content="$t('generic.approve')"
                               icon="nc-icon nc-check-2"
                               @click.stop="approveJob(row)">
          </tooltip-icon-button>
          <tooltip-icon-button type="danger"
                               :content="$t('generic.reject')"
                               icon="nc-icon nc-simple-remove"
                               @click.stop="rejectJob(row)">
          </tooltip-icon-button>
        </template>
      </template>
      <template v-slot:extra-filter-buttons="{lastQuery}" v-if="filterable">
        <base-dropdown position="right">
          <base-button slot="title"
                       icon
                       outline
                       type="primary">
            <i class="nc-icon nc-settings-gear-65"></i>
          </base-button>
          <h6 class="dropdown-header">{{$t('generic.settings')}}</h6>
          <div class="dropdown-item" @click="exportJobs(lastQuery)">
            {{$t('generic.exportToCSV')}}
          </div>
          <div class="dropdown-item" @click="exportJobs(lastQuery, true)">
            {{$t('generic.exportToCSVAll')}}
          </div>
          <div class="dropdown-item" @click="exportJobsXLSX(lastQuery)">
            {{$t('generic.exportToXLSX')}}
          </div>
          <div class="dropdown-item" @click="exportJobsXLSX(lastQuery, true)">
            {{$t('generic.exportToXLSXAll')}}
          </div>

        </base-dropdown>
      </template>

      <template v-slot:title="{row}">
        <div class="d-flex align-items-center">
          <base-avatar :src="row.logo" :name="row.title"></base-avatar>
          <div class="ml-2 font-semibold" data-test="job-list__title">
            {{row.title}}
          </div>
        </div>
      </template>
      <template v-slot:client="{row}">
        <div class="d-flex flex-column">
          <router-link v-if="row.client" @click.native.stop="" :to="`/contacts/clients/${row.client.uuid}`" class="text-capitalize font-semibold">
            {{row.client.name.toLowerCase()}}
          </router-link>
          <div class="text-capitalize">{{row.organisation_name.toLowerCase()}}</div>
        </div>
      </template>
      <template v-slot:kpis.applicants="{row}">
        <el-tooltip :content="$t('job.list.columns.views')" placement="top" :open-delay="300">
          <div class="flex items-center justify-center mr-2">
            <eye-icon class="stats-icon"></eye-icon>
            <span class="stats-text">{{get(row, 'kpis.views', 0)}}</span>
          </div>
        </el-tooltip>
        <el-tooltip :content="$t('job.list.columns.apply_start')" placement="top" :open-delay="300">
          <div class="flex items-center justify-center mr-2">
            <trending-up-icon class="stats-icon"></trending-up-icon>
            <span class="stats-text">{{get(row, 'kpis.apply_start', 0)}}</span>
          </div>
        </el-tooltip>
        <el-tooltip :content="$t('job.list.columns.applicants')" placement="top" :open-delay="300">
          <div class="flex items-center justify-center">
            <file-text-icon class="stats-icon"></file-text-icon>
            <span class="stats-text">{{get(row, 'kpis.applicants', 0)}}</span>
          </div>
        </el-tooltip>

      </template>

      <template v-slot:job_board="{row}">
        <div class="d-flex flex-column">
          <router-link @click.native.stop="" v-for="board in row.job_boards" :key="board.id" :to="`/content/job-board/${board.id}`">
            {{board.name}}
          </router-link>
        </div>
      </template>

      <template v-slot:from="{row}">
        {{$formatDate(row.from, FRIENDLY_DATE_FORMAT)}}
      </template>
      <template v-slot:till="{row}">
        {{$formatDate(row.till, FRIENDLY_DATE_FORMAT)}}
      </template>

      <template v-slot:location_name="{row}">
        {{countryByCode(row.location_name).name || row.location_name}}
      </template>

      <template v-slot:country_code="{row}">
        {{countryByCode(row.country_code).name || $t('generic.na')}}
      </template>

      <template v-slot:status="{row}">
        <status-badge :status="row.status"/>
      </template>

      <template v-slot:product="{row}">
        <div v-if="row.product">
          {{ row.product.name }}
        </div>
      </template>

      <template v-slot:extra-buttons="{row}">
        <tooltip-icon-button icon="el-icon-copy-document"
                             :content="$t('generic.cloneJob')"
                             @click.stop="onCloneJob(row)"
        />
      </template>

    </data-table>
  </div>
</template>
<script>
  import get from 'lodash/get'
  import JobFilters from './JobFilters'
  import JobService from 'src/services/JobService'
  import entities from 'src/config/entities'
  import cloneDeep from 'lodash/cloneDeep'
  import TooltipIconButton from 'src/components/Buttons/TooltipIconButton'
  import { FRIENDLY_DATE_FORMAT, DEFAULT_DATETIME_FULL_FORMAT } from 'src/config/dateUtils'
  import { FileTextIcon, EyeIcon, TrendingUpIcon } from 'vue-feather-icons'

  export default {
    name: 'job-list',
    components: {
      EyeIcon,
      JobFilters,
      TrendingUpIcon,
      FileTextIcon,
      TooltipIconButton,
    },
    props: {
      filters: {
        type: Array,
        default: () => []
      },
      filterable: {
        type: Boolean,
        default: true
      },
      goToAddOnClick: {
        type: Boolean,
        default: true
      },
      showLeftMenu: {
        type: Boolean,
        default: true
      }
    },
    data () {
      return {
        JobService,
        entity: entities.JOB,
        FRIENDLY_DATE_FORMAT,
        DEFAULT_DATETIME_FULL_FORMAT,
      }
    },
    computed: {
      columns () {
        return [
          {
            prop: 'title',
            label: this.$t('job.list.columns.title'),
            minWidth: '240px'
          },
          {
            prop: 'client',
            label: this.$t('job.list.columns.client_name'),
            minWidth: '195px'
          },
          {
            prop: 'product',
            label: this.$t('job.list.columns.product'),
            minWidth: '120px'
          },
          {
            prop: 'from',
            label: this.$t('job.list.columns.from'),
            minWidth: '140px'
          },
          {
            prop: 'till',
            label: this.$t('job.list.columns.till'),
            minWidth: '140px'
          },
          {
            prop: 'kpis.applicants',
            label: this.$t('job.list.columns.stats'),
            minWidth: '180px',
            sortable: true
          },
          {
            prop: 'location_name',
            label: this.$t('job.list.columns.location'),
            minWidth: '140px'
          },
          {
            prop: 'country_code',
            label: this.$t('job.list.columns.country'),
            minWidth: '140px'
          },
          {
            prop: 'job_board',
            label: this.$t('job.list.columns.job_board'),
            minWidth: '160px',
          },
          {
            prop: 'status',
            label: this.$t('job.list.columns.status'),
            minWidth: '120px'
          }
        ]
      },
      jobFilters () {
        return [
          { key: 'with', value: 'client,job_boards' },
          ...this.filters
        ]
      },
      activeCountries () {
        return this.$store.state.config.countries
      }
    },
    methods: {
      get,
      onJobAdd() {
        this.$emit('on-add')
      },
      onRowClick (row, event, column) {
        let exceptions = [this.$t('generic.operations'), this.$t('job.list.columns.client_name'), this.$t('job.list.columns.job_board')]
        if (!exceptions.includes(column.label)) {
          this.$refs.table.navigateToDetails(row)
        }
      },
      onCloneJob(row) {
        const clonedJob = cloneDeep(row)
        const fieldsToDelete = ['uuid', 'custom_fields', 'reference_code', 'created_at', 'updated_by', 'created_by', 'updated_at', 'deleted_at', 'job_url', 'kpis', 'job_boards_url', 'external_reference','client_token_id', 'segment_ids', 'subscription_id', 'slug']
        fieldsToDelete.forEach(key => {
          delete clonedJob[key]
        })
        if (!clonedJob.payment_type_id) {
          clonedJob.payment_type_id = null
        }
        if (!clonedJob.remote) {
          clonedJob.remote = false
        }
        clonedJob.date_order = this.$formatDate(new Date(), DEFAULT_DATETIME_FULL_FORMAT)

        this.$store.commit('jobs/SET_JOB_TO_CLONE', clonedJob)
        this.$router.push('/jobs/create?clone=true')
      },
      countryByCode(code) {
        return this.activeCountries.find(c => c.code === code) || { name: null}
      },
      async approveJob(row) {
        let newStatus = 'active'
        await JobService.verify(row.uuid, {
          status: newStatus
        })
        row.status = newStatus
      },
      async rejectJob(row) {
        let newStatus = 'rejected'
        await JobService.verify(row.uuid, {
          status: newStatus
        })
        row.status = newStatus
      },
      async exportJobs(query, all = false) {
        let data = cloneDeep(query)
        if (all) {
          data.sort.limit = 999
        }
        await JobService.export(data, 'csv')
      },
      async exportJobsXLSX(query, all = false) {
        let data = cloneDeep(query)
        if (all) {
          data.sort.limit = 999
        }
        await JobService.export(data, 'xlsx')
      }
    }
  }
</script>
<style>
</style>
